import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import type {
    IShikshaPipelineRiskBucket,
    IShikshaPipelineRiskResponse,
    IShikshaPipelineRiskStage,
    IShikshaPipelineRiskStageView,
    IShikshaPipelineRiskSummaryView,
    ShikshaPipelineRiskStageKey,
} from '@core/models/shiksha-dashboard.model'
import {
    SHIKSHA_DASHBOARD_API_ENDPOINTS,
    SHIKSHA_DASHBOARD_API_PARAMS,
    SHIKSHA_PIPELINE_RISK_RISK_KEYS,
    SHIKSHA_PIPELINE_RISK_STAGE_ORDER,
    SHIKSHA_PIPELINE_RISK_STAGE_KEYS,
    SHIKSHA_PIPELINE_RISK_STAGE_LABELS,
    SHIKSHA_PIPELINE_RISK_UI,
} from '@features/home/shiksha-components/constants/shiksha-dashboard.constants'
import {
    getPipelineRiskStageDataByKey,
    getPipelineRiskStuckAfterDaysByStageKey,
} from '@features/home/shiksha-components/utils/shiksha-pipeline-risk.util'
import { environment } from '@env/environment'
import type { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

@Injectable({ providedIn: 'root' })
export class ShikshaPipelineRiskService {
    private readonly httpClient = inject(HttpClient)

    getPipelineRiskSummary(
        userId: string,
        financialYear: number,
        scope: 'my' | 'team',
    ): Observable<IShikshaPipelineRiskSummaryView> {
        const endpoint = `${environment.apiBaseUrl}${SHIKSHA_DASHBOARD_API_ENDPOINTS.PIPELINE_RISK}/${financialYear}`
        let params = new HttpParams().set(SHIKSHA_DASHBOARD_API_PARAMS.USER_ID, userId)
        if (scope === 'team') {
            params = params.set(SHIKSHA_DASHBOARD_API_PARAMS.SCOPE, scope)
        }
        return this.httpClient
            .get<IShikshaPipelineRiskResponse>(endpoint, { params })
            .pipe(map((response) => this.mapPipelineRiskResponse(response)))
    }

    private mapPipelineRiskResponse(
        response: IShikshaPipelineRiskResponse | null | undefined,
    ): IShikshaPipelineRiskSummaryView {
        const data = response?.data
        const stages: IShikshaPipelineRiskStageView[] = SHIKSHA_PIPELINE_RISK_STAGE_ORDER.map(
            (stageKey) =>
                this.mapStage(
                    stageKey,
                    SHIKSHA_PIPELINE_RISK_STAGE_LABELS[stageKey],
                    getPipelineRiskStageDataByKey(data, stageKey),
                    getPipelineRiskStuckAfterDaysByStageKey(data, stageKey),
                ),
        )
        const onHoldLeadCount = data?.on_hold?.lead_ids?.length ?? 0
        return { stages, onHoldLeadCount }
    }

    private mapStage(
        stageKey: ShikshaPipelineRiskStageKey,
        stageLabel: string,
        stageData: IShikshaPipelineRiskStage | undefined,
        stuckAfterDays: number | undefined,
    ): IShikshaPipelineRiskStageView {
        const highAmount = this.getAmount(stageData?.high, stageKey)
        const moderateAmount = this.getAmount(stageData?.moderate, stageKey)
        return {
            stageKey,
            stageLabel,
            stuckAfterDays,
            metrics: [
                {
                    riskKey: SHIKSHA_PIPELINE_RISK_RISK_KEYS.HIGH,
                    riskLabel: SHIKSHA_PIPELINE_RISK_UI.highLabel,
                    leadCount: this.getLeadCount(stageData?.high),
                    amount: highAmount,
                },
                {
                    riskKey: SHIKSHA_PIPELINE_RISK_RISK_KEYS.MODERATE,
                    riskLabel: SHIKSHA_PIPELINE_RISK_UI.moderateLabel,
                    leadCount: this.getLeadCount(stageData?.moderate),
                    amount: moderateAmount,
                },
            ],
        }
    }

    private getLeadCount(bucket: IShikshaPipelineRiskBucket | undefined): number {
        return bucket?.lead_ids?.length ?? 0
    }

    private getAmount(
        bucket: IShikshaPipelineRiskBucket | undefined,
        stageKey: ShikshaPipelineRiskStageKey,
    ): number {
        const amount =
            stageKey === SHIKSHA_PIPELINE_RISK_STAGE_KEYS.STAGE_4
                ? (bucket?.order_value ?? 0)
                : (bucket?.total_estimated_amount ?? 0)
        return Number.isFinite(amount) ? Math.max(amount, 0) : 0
    }
}
